import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Command, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { keydownHandler } from '@atlaskit/editor-prosemirror/keymap';

import type { AiAutocompletePlugin } from '../aiAutocompletePluginType';
import { aiAutocompletePluginKey } from '../ui/components/plugin-key';
import { ACTIONS } from '../ui/types/types';
import { addAutocompleteInvokedAnalytics } from '../ui/utils/analytics';

/**
 * Keymap plugin for AI heading autocomplete: manual trigger, accept, dismiss,
 * and dismiss on any other key while a suggestion is active (same behavior as
 * the previous handleDOMEvents keydown handler).
 */
export function createAiAutocompleteKeymapPlugin({
	api,
}: {
	api: ExtractInjectionAPI<AiAutocompletePlugin> | undefined;
}): SafePlugin {
	const editorAnalyticsAPI = api?.analytics?.actions;

	// Mod-Shift-Space maps to Ctrl on Windows/Linux and Cmd on Mac (see prosemirror-keymap
	// normalizeKeyName). Tests and some environments dispatch metaKey+Shift+Space without
	// ctrlKey, so we also bind Meta-Shift-Space explicitly (same as the old DOM check:
	// (metaKey || ctrlKey) && shiftKey && code === 'Space').
	const triggerManualInline: Command = (state, dispatch, view) => {
		if (!dispatch || !view) {
			return false;
		}

		const pluginState = aiAutocompletePluginKey.getState(state);
		if (pluginState && pluginState.status !== 'idle') {
			api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
		}

		const freshState = view.state;
		const resolvedCursorPos = freshState.selection.from;
		const invokeTr = freshState.tr.setMeta(aiAutocompletePluginKey, {
			type: ACTIONS.TRIGGER_MANUAL,
			cursorPos: resolvedCursorPos,
		});
		addAutocompleteInvokedAnalytics(editorAnalyticsAPI)(invokeTr);
		view.dispatch(invokeTr);
		return true;
	};

	const bindings: Record<string, Command> = {
		['Mod-Shift-Space']: triggerManualInline,
		['Meta-Shift-Space']: triggerManualInline,
		['Tab']: (state, dispatch) => {
			const pluginState = aiAutocompletePluginKey.getState(state);
			if (pluginState?.status !== 'suggestion' || pluginState.trigger !== 'manual') {
				return false;
			}
			if (!dispatch) {
				return false;
			}
			return (
				api?.core?.actions?.execute?.(
					api?.aiAutocomplete?.commands?.acceptSuggestion?.({
						cursorPos: pluginState.cursorPos,
						suggestion: pluginState.suggestion,
					}),
				) ?? false
			);
		},
		['Escape']: (state, dispatch) => {
			const pluginState = aiAutocompletePluginKey.getState(state);
			if (!pluginState || pluginState.status === 'idle') {
				return false;
			}
			if (!dispatch) {
				return false;
			}
			return (
				api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.()) ?? false
			);
		},
	};

	return new SafePlugin({
		props: {
			handleKeyDown(view, event) {
				if (keydownHandler(bindings)(view, event)) {
					return true;
				}

				const pluginState = aiAutocompletePluginKey.getState(view.state);
				if (!pluginState || pluginState.status === 'idle') {
					return false;
				}

				api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
				return false;
			},
		},
	});
}
